import { Link } from '@tanstack/react-router';
import {
    ArrowLeft,
    MoreHorizontal,
    Pause,
    Pencil,
    Play,
    Trash2,
} from 'lucide-react';
import { type ReactElement, type ReactNode } from 'react';

import { StatusBadge } from '@apps/web/components/automations/badges';
import { automationScheduleSummary } from '@apps/web/components/automations/customer/automation_schedule_summary';
import { type CoordinatorAutomationRead } from '@apps/web/components/automations/types';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@apps/web/components/ui/table';
import { formatDateAndTime } from '@apps/web/lib/date_utils';

function MetadataRow({
    label,
    children,
}: {
    readonly label: string;
    readonly children: ReactNode;
}): ReactElement {
    return (
        <TableRow className="border-0 hover:bg-transparent">
            <TableCell className="text-muted-foreground w-[140px] pl-0 align-top font-medium">
                {label}
            </TableCell>
            <TableCell className="border-0 pl-0">{children}</TableCell>
        </TableRow>
    );
}

export function AutomationDetailHeader({
    automation,
    isTogglingEnabled,
    onDelete,
    onEdit,
    onToggleEnabled,
    timezone,
}: {
    readonly automation: CoordinatorAutomationRead;
    readonly isTogglingEnabled: boolean;
    readonly onDelete: () => void;
    readonly onEdit: () => void;
    readonly onToggleEnabled: () => void;
    readonly timezone: string;
}): ReactElement {
    return (
        <header className="space-y-4">
            <Link
                to="/routines"
                className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm"
            >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Routines
            </Link>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <h1 className="text-foreground min-w-0 text-2xl font-semibold tracking-tight">
                    {automation.name}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" onClick={onEdit}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={isTogglingEnabled}
                        onClick={onToggleEnabled}
                    >
                        {isTogglingEnabled ? (
                            <Spinner size="xs" className="mr-2" />
                        ) : automation.enabled ? (
                            <Pause className="mr-2 h-4 w-4" />
                        ) : (
                            <Play className="mr-2 h-4 w-4" />
                        )}
                        {automation.enabled ? 'Pause' : 'Resume'}
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild={true}>
                            <Button
                                variant="outline"
                                size="sm"
                                aria-label="More routine actions"
                            >
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={onDelete}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            <Table className="text-sm">
                <TableBody>
                    <MetadataRow label="Status">
                        <StatusBadge
                            status={automation.enabled ? 'active' : 'paused'}
                        />
                    </MetadataRow>
                    <MetadataRow label="Schedule">
                        {automationScheduleSummary({
                            schedule: automation.schedule,
                            timezone,
                        })}
                    </MetadataRow>
                    <MetadataRow label="Next run">
                        {automation.next_fire_at === null
                            ? '—'
                            : formatDateAndTime(
                                  automation.next_fire_at,
                                  timezone,
                              )}
                    </MetadataRow>
                    <MetadataRow label="Created">
                        {formatDateAndTime(automation.created_at, timezone)}
                    </MetadataRow>
                </TableBody>
            </Table>
        </header>
    );
}
